import { ServiceTopology } from 'viewer-manager-interface'
import { IComponentsRegistrar } from '@wix/thunderbolt-components-loader'

import {
	registry,
	createRegistryAPI,
	getComponentsLibraries,
	IThunderboltRegistryAPI,
	IInfoComponentModel,
} from '@wix/editor-elements-registry/thunderbolt'
import { ComponentsRegistryError, ComponentsRegistryErrorTypes } from './errors'
import { getGlobalRegistryRuntime } from './runtime'

type IComponentsRegistryAPI = IThunderboltRegistryAPI

export { IComponentsRegistryAPI }

/**
 * Components Registry for Thunderbolt Client Side Rendering
 */
export interface IComponentsRegistryCSR {
	getRegistryAPI: () => IComponentsRegistryAPI
	/**
	 * Legacy API is used for migrating to the thunderbolt + registry integration
	 */
	getLegacyComponentsRegistrarAPI: () => IComponentsRegistrar
}

export async function createComponentsRegistryCSR(
	options: {
		serviceTopology?: ServiceTopology
		url?: string
	} = {}
): Promise<IComponentsRegistryCSR> {
	const { serviceTopology, url } = options

	const runtime = getGlobalRegistryRuntime<IInfoComponentModel>()

	if (!runtime) {
		if (!serviceTopology) {
			throw new ComponentsRegistryError(
				'"serviceTopology" or "runtime" is required to create registry',
				ComponentsRegistryErrorTypes.INVALID_ARGUMENTS
			)
		}
	}

	const registryAPI = await createRegistryAPI(registry, {
		shouldLoadCSSAssets: true,
		/**
		 * using `lazy` registry for both SSR & CSR
		 *
		 * under the hood, it will use batch bundles,
		 * the rest components (that are not included at the batch), will be inlined in the head on SSR
		 * or lazy fetched on CSR
		 *
		 * Current issue – we don't recommend to use different modes on SSR and CSR
		 * because of the probability of the duplicated code in different bundles.
		 * Will be fixed in the next registry PRs
		 *
		 * https://jira.wixpress.com/browse/WCR-364
		 */
		mode: 'lazy',
		...(runtime ? runtime : { libraries: getComponentsLibraries(serviceTopology as any, url) }),
	})

	return {
		getLegacyComponentsRegistrarAPI() {
			return {
				registerComponents: (hostAPI) => {
					registryAPI.forEachComponent((componentName, loader) => {
						/**
						 * TODO: hardcoded split should be removed after "specs.thunderbolt.componentsRegistry" experiment is merged
						 */
						const [name, uiType] = componentName.split('_')

						hostAPI.registerComponent(
							name,
							async () => {
								const model = await loader()

								return {
									default: model.component,
									component: model.component,
									controller: model.controller,
								}
							},
							uiType
						)
					})
				},
			}
		},

		getRegistryAPI() {
			return registryAPI
		},
	}
}
